// src/utils/filtresEquipements.ts — SCE Issues
// Filtres de la page Équipements (05/10, maquette validée :
// https://claude.ai/artifact/ArzUFTyfjXggN1i2MPFScR).
//
// ─── Les critères ───
//
// Général : fabrication (05/10 : liste lue dans les dossiers de production,
// `utils/enFabrication`), type, gamme, exécution (modulaire / Custom), zone
// dangereuse, chef de projet, problèmes. Puis un critère par position du code de désignation
// (`utils/codeDesignation`) : les colonnes d'un côté, les systèmes Hipersep de
// l'autre. Le diamètre se filtre par tranches (décision de Guillaume du 05/10).
//
// Plusieurs valeurs d'un même critère = OU ; plusieurs critères = ET. Un
// critère qui ne concerne pas l'équipement (la pression d'un Flowdrive) l'écarte
// dès qu'il filtre. « Non renseigné » = l'équipement est concerné mais son code
// ne dit rien à cette position (code absent, coupé dans Windchill) : il revient
// en cochant cette valeur.
//
// Les nombres d'un critère tiennent compte des AUTRES critères (pas du sien),
// comme dans le tableau « Tous les problèmes ».
//
// Pur : aucun réseau, aucun React.

import {
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  suffixeNumeroSerie,
  type Equipement,
} from '@/types/issue.types'
import {
  TYPES_EQUIPEMENT,
  TYPE_PAR_CATEGORIE,
  modeleParLibelle,
  nvsDeFourniture,
  type ModeleEquipement,
} from '@/config/equipements.catalogue'
import {
  decoderDesignation,
  positionsDe,
  type DesignationDecodee,
  type Execution,
  type FamilleCode,
} from './codeDesignation'

export const NON_RENSEIGNE = 'Non renseigné'
export const EN_FABRICATION = 'En fabrication'
export const HORS_FABRICATION = 'Hors fabrication'

// ─── L'équipement, lu une fois ───

export interface EquipementLu {
  equipement: Equipement
  modele: ModeleEquipement | null
  decode: DesignationDecodee | null
  /** Colonne, système Hipersep, ou ni l'un ni l'autre (BioSC, Slurry…). */
  famille: FamilleCode | null
  nvs: string
  nbProblemes: number
  /** Dans la liste des dossiers de production en cours. */
  enFabrication: boolean
}

export function lireEquipement(equipement: Equipement, nbProblemes: number, enFabrication = false): EquipementLu {
  const categorie = CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(equipement.numeroSerie)] ?? null
  const modele = modeleParLibelle(equipement.familleEquipement)
  const decode = decoderDesignation(equipement.designation)
  // Sans code lisible, la catégorie (suffixe) ou le modèle disent encore la famille :
  // l'équipement est alors « Non renseigné » sur les critères du code, pas absent.
  const famille: FamilleCode | null =
    decode?.famille ??
    (categorie === 'Colonne'
      ? 'colonne'
      : modele && /^(Hipersep|Resolute) Flowdrive/.test(modele.libelle)
        ? 'hipersep'
        : null)
  return {
    equipement,
    modele,
    decode,
    famille,
    nvs: nvsDeFourniture(equipement.fourniture, categorie),
    nbProblemes,
    enFabrication,
  }
}

/** Modulaire ou Custom d'après la base du code : Z en tête = Custom (BioSC et Booster compris). */
export function executionDe(e: EquipementLu): Execution | null {
  if (e.decode) return e.decode.execution
  const code = (e.equipement.designation ?? '').replace(/\s+/g, '').toUpperCase()
  if (/^(ZIO|ZOS)/.test(code)) return 'Custom'
  if (/^(BIO|BOS)/.test(code)) return 'Modulaire'
  return null
}

/** Valeur décodée d'une position ; null si le code ne va pas jusque-là. */
function champ(e: EquipementLu, libelle: string): string | null {
  const c = e.decode?.champs.find((x) => x.libelle === libelle)
  if (!c) return null
  return c.valeur ?? `Inconnu (${c.code})`
}

// ─── Critères ───

export type SectionCritere = 'general' | 'colonne' | 'hipersep'

export interface Critere {
  id: string
  libelle: string
  /** Libellé du raccourci de la barre, quand il diffère. */
  court?: string
  section: SectionCritere
  /** Valeurs de l'équipement pour ce critère ; vide = pas concerné. */
  valeurs: (e: EquipementLu) => readonly string[]
  /** Ordre d'affichage des valeurs ; les autres suivent, Custom et Non renseigné à la fin. */
  ordre?: readonly string[]
}

const DIAMETRE = 'Diamètre intérieur'
export const TRANCHES_DIAMETRE = ['≤ 200 mm', '300 à 600 mm', '≥ 800 mm'] as const

function trancheDiametre(e: EquipementLu): string {
  const c = e.decode?.champs.find((x) => x.libelle === DIAMETRE)
  if (!c) return NON_RENSEIGNE
  if (/^Z+$/.test(c.code)) return 'Custom'
  if (!/^\d+$/.test(c.code)) return `Inconnu (${c.code})`
  const mm = Number.parseInt(c.code, 10)
  return mm <= 200 ? TRANCHES_DIAMETRE[0] : mm < 800 ? TRANCHES_DIAMETRE[1] : TRANCHES_DIAMETRE[2]
}

/** Un critère par position du code, sauf la base (type, gamme, exécution la disent) et la zone (générale). */
function criteresDuCode(famille: FamilleCode): Critere[] {
  return positionsDe(famille)
    .filter((p) => p.libelle !== 'Base' && p.libelle !== 'Zone dangereuse')
    .map((p) => {
      const diametre = famille === 'colonne' && p.libelle === DIAMETRE
      return {
        id: `${famille}:${p.libelle}`,
        libelle: p.libelle,
        section: famille,
        valeurs: (e: EquipementLu) =>
          e.famille !== famille ? [] : [diametre ? trancheDiametre(e) : (champ(e, p.libelle) ?? NON_RENSEIGNE)],
        ordre: diametre ? TRANCHES_DIAMETRE : p.ordre,
      }
    })
}

export const CRITERE_PRESSION = 'colonne:Pression de service'
export const CRITERE_DIAMETRE = `colonne:${DIAMETRE}`

export const CRITERE_FABRICATION = 'fabrication'

export const CRITERES: readonly Critere[] = [
  {
    id: CRITERE_FABRICATION,
    libelle: 'Fabrication',
    section: 'general',
    valeurs: (e) => [e.enFabrication ? EN_FABRICATION : HORS_FABRICATION],
    ordre: [EN_FABRICATION, HORS_FABRICATION],
  },
  {
    id: 'type',
    libelle: 'Type',
    section: 'general',
    valeurs: (e) => {
      const categorie = CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(e.equipement.numeroSerie)]
      return [categorie ? TYPE_PAR_CATEGORIE[categorie] : (e.modele?.type ?? NON_RENSEIGNE)]
    },
    ordre: TYPES_EQUIPEMENT,
  },
  {
    id: 'gamme',
    libelle: 'Gamme',
    section: 'general',
    valeurs: (e) => [e.modele?.gamme ?? NON_RENSEIGNE],
    ordre: ['Hipersep', 'Resolute', 'Commun'],
  },
  {
    id: 'execution',
    libelle: 'Exécution',
    section: 'general',
    valeurs: (e) => [executionDe(e) ?? NON_RENSEIGNE],
    ordre: ['Modulaire', 'Custom'],
  },
  {
    id: 'zone',
    libelle: 'Zone dangereuse',
    section: 'general',
    valeurs: (e) => (e.famille ? [champ(e, 'Zone dangereuse') ?? NON_RENSEIGNE] : []),
    ordre: ['Non', 'ATEX zone 2', 'ATEX zone 1', 'Class 1 Div 2', 'Class 1 Div 1', 'IECEx zone 2', 'IECEx zone 1'],
  },
  {
    id: 'chefProjet',
    libelle: 'Chef de projet',
    section: 'general',
    valeurs: (e) => [e.equipement.chefProjet?.trim() || NON_RENSEIGNE],
  },
  {
    id: 'problemes',
    libelle: 'Problèmes',
    court: 'Avec problèmes',
    section: 'general',
    valeurs: (e) =>
      e.nbProblemes >= 2
        ? ['Avec problèmes', 'Récurrents (2 et plus)']
        : e.nbProblemes === 1
          ? ['Avec problèmes']
          : ['Sans problème'],
    ordre: ['Avec problèmes', 'Récurrents (2 et plus)', 'Sans problème'],
  },
  ...criteresDuCode('colonne').map((c) =>
    c.id === CRITERE_PRESSION ? { ...c, court: 'Pression' } : c.id === CRITERE_DIAMETRE ? { ...c, court: 'Diamètre' } : c,
  ),
  ...criteresDuCode('hipersep'),
]

const PAR_ID = new Map(CRITERES.map((c) => [c.id, c]))

export function critere(id: string): Critere | undefined {
  return PAR_ID.get(id)
}

/**
 * Les raccourcis de la barre (maquette validée) ; le reste est dans « Tous les
 * filtres ». La fabrication a sa propre pastille, une bascule en tête de barre.
 */
export const RACCOURCIS: readonly string[] = [
  'type',
  'execution',
  CRITERE_PRESSION,
  CRITERE_DIAMETRE,
  'zone',
  'chefProjet',
  'problemes',
]

export const LIBELLES_SECTIONS: Record<SectionCritere, string> = {
  general: 'Général',
  colonne: 'Colonnes',
  hipersep: 'Systèmes Hipersep',
}

// ─── Sélection ───

/** Valeurs cochées par critère. Un critère sans valeur ne filtre pas. */
export type SelectionFiltres = Readonly<Record<string, readonly string[]>>

export function nbCriteresActifs(selection: SelectionFiltres, ids?: readonly string[]): number {
  return Object.entries(selection).filter(([id, v]) => v.length > 0 && (!ids || ids.includes(id))).length
}

export function basculer(selection: SelectionFiltres, id: string, valeur: string): SelectionFiltres {
  const avant = selection[id] ?? []
  const apres = avant.includes(valeur) ? avant.filter((v) => v !== valeur) : [...avant, valeur]
  const { [id]: _retire, ...reste } = selection
  void _retire
  return apres.length > 0 ? { ...reste, [id]: apres } : reste
}

export function effacer(selection: SelectionFiltres, id: string): SelectionFiltres {
  const { [id]: _retire, ...reste } = selection
  void _retire
  return reste
}

// ─── Recherche, filtre, comptage ───

/** PN, OF, N° de série, CVE, NVS, modèle, code de désignation, chef de projet. */
export function correspondRecherche(e: EquipementLu, recherche: string): boolean {
  const q = cleRapprochement(recherche)
  if (!q) return true
  const eq = e.equipement
  return [eq.numeroSerie, eq.of, eq.pn, eq.cve, eq.designation, eq.familleEquipement, eq.chefProjet, e.nvs].some(
    (v) => cleRapprochement(v).includes(q),
  )
}

function passe(e: EquipementLu, selection: SelectionFiltres, sauf?: string): boolean {
  for (const [id, choisies] of Object.entries(selection)) {
    if (id === sauf || choisies.length === 0) continue
    const c = PAR_ID.get(id)
    if (!c) continue
    if (!c.valeurs(e).some((v) => choisies.includes(v))) return false
  }
  return true
}

export function filtrer(
  lus: readonly EquipementLu[],
  selection: SelectionFiltres,
  recherche: string,
  sauf?: string,
): EquipementLu[] {
  return lus.filter((e) => correspondRecherche(e, recherche) && passe(e, selection, sauf))
}

/** Nombre d'équipements par valeur du critère, avec tous les AUTRES filtres. */
export function compterValeurs(
  lus: readonly EquipementLu[],
  selection: SelectionFiltres,
  recherche: string,
  id: string,
): Map<string, number> {
  const c = PAR_ID.get(id)
  const n = new Map<string, number>()
  if (!c) return n
  for (const e of filtrer(lus, selection, recherche, id)) {
    for (const v of new Set(c.valeurs(e))) n.set(v, (n.get(v) ?? 0) + 1)
  }
  return n
}

/** Les valeurs présentes dans le référentiel (et celles cochées), dans l'ordre du critère. */
export function valeursPossibles(lus: readonly EquipementLu[], id: string, selection: SelectionFiltres = {}): string[] {
  const c = PAR_ID.get(id)
  if (!c) return []
  const vues = new Set<string>(selection[id] ?? [])
  for (const e of lus) for (const v of c.valeurs(e)) vues.add(v)
  const ordre = c.ordre ?? []
  const fin = (v: string): number => (v === NON_RENSEIGNE ? 3 : v.startsWith('Inconnu') ? 2 : v === 'Custom' ? 1 : 0)
  const rang = (v: string): number => {
    const i = ordre.indexOf(v)
    return i === -1 ? ordre.length : i
  }
  return [...vues].sort(
    (a, b) => fin(a) - fin(b) || rang(a) - rang(b) || a.localeCompare(b, 'fr', { numeric: true }),
  )
}

// ─── Résumé des caractéristiques (liste) ───

export interface MorceauResume {
  /** Critère dont le morceau parle : il passe en gras quand ce critère filtre. */
  critere: string
  texte: string
}

/**
 * Ce qui distingue l'équipement, en une ligne : « Ø 450 · 100 bar · fût 700 ·
 * ATEX zone 2 » pour une colonne, « Process M · 6 entrées · 15 collectes »
 * pour un système Hipersep. Les options à « Non » sont tues.
 */
export function resumeCaracteristiques(e: EquipementLu): MorceauResume[] {
  const d = e.decode
  if (!d) return []
  const code = (libelle: string): string | undefined => d.champs.find((c) => c.libelle === libelle)?.code
  // Une valeur hors document n'a rien à faire dans une ligne de liste : tue.
  const connu = (libelle: string): string | null => d.champs.find((c) => c.libelle === libelle)?.valeur ?? null
  const morceaux: MorceauResume[] = []
  // « Custom » seul ne dit pas de quoi il parle dans une ligne : « zone Custom ».
  const ajouter = (critereId: string, texte: string | null | undefined, nom?: string): void => {
    if (texte) morceaux.push({ critere: critereId, texte: texte === 'Custom' && nom ? `${nom} Custom` : texte })
  }
  const zone = connu('Zone dangereuse')
  if (d.famille === 'colonne') {
    const diametre = code(DIAMETRE)
    if (diametre) ajouter(CRITERE_DIAMETRE, /^\d+$/.test(diametre) ? `Ø ${Number.parseInt(diametre, 10)}` : 'Ø Custom')
    ajouter(CRITERE_PRESSION, connu('Pression de service'), 'pression')
    const fut = connu('Hauteur de fût')
    if (fut) ajouter('colonne:Hauteur de fût', `fût ${fut.replace(/ mm$/, '')}`)
    if (zone && zone !== 'Non') ajouter('zone', zone, 'zone')
    const enveloppe = code('Double enveloppe, température')
    if (enveloppe === 'A' || enveloppe === 'C') ajouter('colonne:Double enveloppe, température', 'double enveloppe')
  } else {
    ajouter('hipersep:Taille', connu('Taille'), 'taille')
    if (zone && zone !== 'Non') ajouter('zone', zone, 'zone')
    ajouter('hipersep:Entrées de la pompe principale', connu('Entrées de la pompe principale'), 'entrées')
    ajouter('hipersep:Collectes', connu('Collectes'), 'collectes')
    const injection = connu('Injection HP')
    if (injection && injection !== 'Non') ajouter('hipersep:Injection HP', 'injection HP')
  }
  return morceaux
}
